import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X, Edit } from "lucide-react";
import DetailedApplicationViewer from "./expert-applications/DetailedApplicationViewer";
import WorkflowControls from "./expert-applications/WorkflowControls";
import { useExpertApplicationEdit } from "@/hooks/useExpertApplicationEdit";

import { ExpertApplication } from "@/types/admin";

interface ExpertApplicationDetailsProps {
  application: ExpertApplication;
  adminNotes: string;
  setAdminNotes: (notes: string) => void;
  onStatusUpdate: (applicationId: string, status: 'approved' | 'rejected' | 'under_review') => void;
  onDelete: (applicationId: string) => void;
  onClose: () => void;
}

export default function ExpertApplicationDetails({
  application,
  adminNotes,
  setAdminNotes,
  onStatusUpdate,
  onDelete,
  onClose
}: ExpertApplicationDetailsProps) {
  const {
    isEditing,
    setIsEditing,
    editedApplication,
    setEditedApplication,
    loading: editLoading,
    handleSaveEdit,
    cancelEdit
  } = useExpertApplicationEdit(application);

  return (
    <div className="space-y-6">
      {/* Header Card with Close Button */}
      <Card className="mb-2">
        <CardHeader className="pb-4 px-6 pt-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Application Review</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Detailed Application Viewer */}
      <div className="mb-2">
        <DetailedApplicationViewer application={application} />
      </div>

      {/* Workflow Controls - This already includes the EnhancedPublishingSection */}
      <div className="mb-2">
        <WorkflowControls
          application={application}
          onStatusUpdate={onStatusUpdate}
          loading={editLoading}
        />
      </div>

      {/* Admin Notes */}
      <Card className="p-6">
        <CardHeader className="pb-2 px-0">
          <CardTitle>Admin Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-0">
          <div>
            <Label htmlFor="admin-notes" className="text-sm font-medium">Add Notes</Label>
            <Textarea
              id="admin-notes"
              placeholder="Add notes about this application..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="mt-2"
              rows={3}
            />
          </div>

          {application.admin_notes && (
            <div>
              <Label className="text-sm font-medium">Previous Notes</Label>
              <div className="mt-2 p-3 bg-muted rounded-lg text-sm">
                {application.admin_notes}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
